import React, { useMemo } from 'react';
import { Card, Descriptions, Typography, Space, Tag, Alert, Image } from 'antd';
import { 
  UserOutlined, 
  PhoneOutlined, 
  IdcardOutlined, 
  DollarOutlined,
  CameraOutlined,
  ShoppingOutlined,
  EnvironmentOutlined,
  FileOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';

const { Title, Text, Paragraph } = Typography;

interface IndividualReviewFormProps {
  categoryId: number;
  categoryProducts?: any[];
  categoryLocations?: any[];
  categoryDocuments?: any[];
}

const IndividualReviewForm: React.FC<IndividualReviewFormProps> = ({
  categoryId,
  categoryProducts = [],
  categoryLocations = [],
  categoryDocuments = [],
}) => {
  const { formData, steps } = useOnboardingStore();

  // Organize data by step groups
  const organizedData = useMemo(() => {
    const data: Record<string, any> = {};

    // Get all field group steps
    const fieldGroupSteps = steps.filter(step => step.component === 'FieldGroup' && step.fieldGroup);

    fieldGroupSteps.forEach(step => {
      if (step.fieldGroup) {
        const groupData: Record<string, any> = {};
        
        step.fieldGroup.fields?.forEach(field => {
          if (!field.fieldName) return;
          const value = formData.customerData[field.fieldName];
          
          // Skip undefined, null, empty string, and empty objects
          if (value === undefined || value === null || value === '') {
            return;
          }
          
          // Skip empty objects
          if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
            return;
          }
          
          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) {
            return;
          }
          
          groupData[field.fieldName] = {
            label: field.label,
            value: value,
            type: field.type,
          };
        });

        if (Object.keys(groupData).length > 0) {
          data[step.fieldGroup.groupKey ?? step.id] = {
            title: step.fieldGroup.groupName,
            description: step.fieldGroup.description,
            icon: step.fieldGroup.icon,
            fields: groupData,
          };
        }
      }
    });

    return data;
  }, [formData.customerData, steps]);

  // Format field value for display
  const formatValue = (value: any, type?: string): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    
    // Handle Day.js/Moment.js objects (check for $d property or format method)
    if (typeof value === 'object' && (value.$d || typeof value.format === 'function')) {
      try {
        // Try to use format method if available
        if (typeof value.format === 'function') {
          return value.format('YYYY-MM-DD');
        }
        // Try to access internal date object
        if (value.$d instanceof Date) {
          return value.$d.toLocaleDateString();
        }
        // Fallback to converting to Date
        return new Date(value).toLocaleDateString();
      } catch {
        return 'Invalid Date';
      }
    }
    
    // Handle objects and arrays
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'N/A';
      }
      // For objects, check if empty
      if (Object.keys(value).length === 0) {
        return 'N/A';
      }
      // Try to stringify non-empty objects
      try {
        return JSON.stringify(value);
      } catch {
        return '[Complex Object]';
      }
    }
    
    switch (type) {
      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return String(value);
        }
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      case 'boolean':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  };

  // Get icon for group
  const getGroupIcon = (iconName?: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'user': <UserOutlined />,
      'phone': <PhoneOutlined />,
      'idcard': <IdcardOutlined />,
      'dollar': <DollarOutlined />,
      'camera': <CameraOutlined />,
    };
    return iconMap[iconName || 'user'] || <UserOutlined />;
  };

  return (
    <div className="individual-review-form" style={{ padding: '24px 0' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={3}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              Review & Submit
            </Title>
            <Paragraph type="secondary">
              Please review all the information below before submitting. You can go back to any step to make changes.
            </Paragraph>
          </Space>
        </Card>

        {/* Field Groups Data */}
        {Object.entries(organizedData).map(([groupKey, groupInfo]) => (
          <Card 
            key={groupKey}
            title={
              <Space>
                {getGroupIcon(groupInfo.icon)}
                <span>{groupInfo.title}</span>
              </Space>
            }
            size="small"
          >
            {groupInfo.description && (
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                {groupInfo.description}
              </Paragraph>
            )}
            <Descriptions bordered column={2} size="small">
              {Object.entries(groupInfo.fields).map(([fieldName, fieldInfo]: [string, any]) => {
                const formattedValue = formatValue(fieldInfo.value, fieldInfo.type);
                
                return (
                  <Descriptions.Item 
                    key={fieldName} 
                    label={fieldInfo.label}
                    span={fieldInfo.type === 'textarea' ? 2 : 1}
                  >
                    {fieldInfo.type === 'file' && fieldInfo.value ? (
                      <Space>
                        <Image
                          src={`/api/files/preview/documents/${fieldInfo.value}`}
                          alt={fieldInfo.label}
                          width={100}
                          height={100}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                        <Text type="secondary">File uploaded</Text>
                      </Space>
                    ) : (
                      <Text>{formattedValue}</Text>
                    )}
                  </Descriptions.Item>
                );
              })}
            </Descriptions>
          </Card>
        ))}

        {/* Products */}
        {formData.productData && formData.productData.length > 0 && (
          <Card 
            title={
              <Space>
                <ShoppingOutlined />
                <span>Selected Products</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.productData.map((product: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    {product.productCode && (
                      <Descriptions.Item label="Product Code">
                        <Tag color="blue">{String(product.productCode)}</Tag>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Product Name">
                      <Text>{String(product.productName || 'N/A')}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Product Type">
                      <Text>{String(product.productType || 'N/A')}</Text>
                    </Descriptions.Item>
                    {product.interestRate && (
                      <Descriptions.Item label="Interest Rate">
                        <Text>{String(product.interestRate)}%</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Locations */}
        {formData.locationData && formData.locationData.length > 0 && (
          <Card 
            title={
              <Space>
                <EnvironmentOutlined />
                <span>Address Information</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.locationData.map((location: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Address Type">
                      <Tag color={location.isPrimary ? 'green' : 'default'}>
                        {String(location.addressType || 'PHYSICAL')}
                        {location.isPrimary && ' (Primary)'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Configuration">
                      <Text>{String(location.locationConfigName || 'N/A')}</Text>
                    </Descriptions.Item>
                    {location.locationSelections && typeof location.locationSelections === 'object' && Object.keys(location.locationSelections).length > 0 && (
                      <Descriptions.Item label="Location" span={2}>
                        <Text>
                          {Object.entries(location.locationSelections)
                            .map(([key, value]: [string, any]) => {
                              // Handle nested objects (like zone, region, etc.)
                              if (value && typeof value === 'object' && 'name' in value) {
                                return `${key}: ${value.name}`;
                              }
                              // Handle primitive values
                              if (value !== null && value !== undefined) {
                                return `${key}: ${String(value)}`;
                              }
                              return null;
                            })
                            .filter(item => item !== null)
                            .join(', ')}
                        </Text>
                      </Descriptions.Item>
                    )}
                    {location.houseNumber && (
                      <Descriptions.Item label="House Number">
                        <Text>{String(location.houseNumber)}</Text>
                      </Descriptions.Item>
                    )}
                    {location.postalCode && (
                      <Descriptions.Item label="Postal Code">
                        <Text>{String(location.postalCode)}</Text>
                      </Descriptions.Item>
                    )}
                    {location.gpsCoordinates && typeof location.gpsCoordinates === 'string' && (
                      <Descriptions.Item label="GPS Coordinates" span={2}>
                        <Text>{location.gpsCoordinates}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Documents */}
        {formData.documentData && formData.documentData.length > 0 && (
          <Card 
            title={
              <Space>
                <FileOutlined />
                <span>Uploaded Documents</span>
              </Space>
            }
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {formData.documentData.map((document: any, index: number) => (
                <Card key={index} size="small" type="inner">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Document Type">
                      <Tag color="blue">{String(document.documentTypeCode || 'N/A')}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Document Name">
                      <Text>{String(document.documentTypeName || 'N/A')}</Text>
                    </Descriptions.Item>
                    {document.fileName && (
                      <Descriptions.Item label="File Name">
                        <Text>{String(document.fileName)}</Text>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Required">
                      {document.isRequired ? (
                        <Tag color="red">Required</Tag>
                      ) : (
                        <Tag>Optional</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        {/* Completion Status */}
        <Card>
          <Alert
            message="Ready to Submit"
            description="All required information has been collected. Click 'Submit' to create the customer account."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Card>
      </Space>
    </div>
  );
};

export default IndividualReviewForm;
